import { useEffect, useState, useCallback, useMemo, useRef } from "react";
import { useSearchParams } from "react-router-dom";
import {
  ReactFlow, Background, Controls, MiniMap, Handle, Position,
  addEdge, useNodesState, useEdgesState, MarkerType,
} from "@xyflow/react";
import "@xyflow/react/dist/style.css";
import { api } from "@/lib/api";
import {
  Network, Plus, X, Loader2, AlertTriangle, Sparkles, Trash2, Edit2,
  User, Building2, Cpu, Server, Zap, Crown, Eye, EyeOff, Coins, Scale, ShieldCheck,
  Clock, GitMerge, ArrowUp, ArrowDown, LifeBuoy, Map, Check, Link2,
} from "lucide-react";

// P0 fractal — Position de capture de valeur dans la chaîne.
// L'asymétrie peut se monnayer en amont (contrôler l'entrée), en aval
// (contrôler la sortie), ou en "soutenant le soutien" (être l'infra
// indispensable d'un intermédiaire). Aucune position n'est intrinsèquement
// supérieure — la rente naît là où l'on est non-remplaçable.
const POSITION_CAPTURE = {
  amont:   { label: "Amont",                icon: ArrowUp,   color: "#1D9E75", desc: "Je contrôle une ressource, un accès, une donnée à l'entrée du flux." },
  aval:    { label: "Aval",                 icon: ArrowDown, color: "#378ADD", desc: "Je contrôle la distribution, la décision finale, la sortie." },
  soutien: { label: "Soutien du soutien",   icon: LifeBuoy,  color: "#EF9F27", desc: "Je suis l'infrastructure indispensable d'un intermédiaire — invisible mais non-remplaçable." },
};
import { notify, fmtError } from "@/lib/notify";
import PitchCaptureButton from "@/components/PitchCaptureButton";
import { useTranslation } from "react-i18next";
import AsymmetryEducationPanel from "@/components/AsymmetryEducationPanel";
import BrasArmeAnalysisPanel from "@/components/BrasArmeAnalysisPanel";
import FreshnessBadge from "@/components/FreshnessBadge";
import ThreeQPageHeader from "@/components/ThreeQPageHeader";

// P0 — Catalogue local des 10 types d'asymétrie monétisable (icônes lucide).
// Le serveur renvoie le même catalogue dans graph.asymmetry_catalog.
const ASYM_TYPES = {
  information:    { label: "Information",    icon: Eye,         color: "#378ADD", example: "Bloomberg, Reuters", explanation: "Vous détenez une information que les autres n'ont pas — ou pas à temps. Elle vous rend incontournable dans le flux de décision." },
  reseau:         { label: "Réseau",         icon: Network,     color: "#EF9F27", example: "Family office, dealmaker", explanation: "Votre valeur vient de votre carnet d'adresses : vous connectez des acteurs qui ne se parleraient pas sans vous." },
  capital:        { label: "Capital",        icon: Coins,       color: "#1D9E75", example: "Banque, PE", explanation: "Vous contrôlez l'accès aux ressources financières. Sans votre feu vert, le projet ne se finance pas." },
  technologie:    { label: "Technologie",    icon: Cpu,         color: "#7F77DD", example: "Nvidia, OpenAI", explanation: "Vous maîtrisez une technologie difficile à répliquer. Les autres en dépendent pour opérer." },
  reglementaire:  { label: "Réglementaire",  icon: Scale,       color: "#C26EE7", example: "Juriste spécialisé", explanation: "Vous détenez une expertise ou une autorisation réglementaire sans laquelle l'activité est bloquée." },
  confiance:      { label: "Confiance",      icon: ShieldCheck, color: "#22D3EE", example: "Notaire, Big4", explanation: "Votre signature ou votre label crée la confiance : les tiers s'y fient pour valider une transaction." },
  infrastructure: { label: "Infrastructure", icon: Server,      color: "#A78BFA", example: "AWS, SIX SIS", explanation: "Vous êtes le socle technique/opérationnel sur lequel les autres s'appuient — invisible mais non-remplaçable." },
  temps:          { label: "Temps",          icon: Clock,       color: "#F472B6", example: "Anticipation, Radar", explanation: "Votre avantage est l'anticipation : vous voyez et agissez avant les autres." },
  execution:      { label: "Exécution",      icon: Zap,         color: "#FACC15", example: "Agir vite", explanation: "Votre force est la capacité à exécuter vite et bien là où les autres tardent ou échouent." },
  coordination:   { label: "Coordination",   icon: GitMerge,    color: "#FB7185", example: "Chef d'orchestre", explanation: "Vous orchestrez plusieurs acteurs. Sans votre coordination, l'ensemble se disloque." },
};

function asymColor(t) { return ASYM_TYPES[t]?.color || "#71717a"; }

const ACTOR_TYPES = [
  { id: "personne", label: "Personne", icon: User },
  { id: "organisation", label: "Organisation", icon: Building2 },
  { id: "systeme", label: "Système", icon: Cpu },
  { id: "infrastructure", label: "Infrastructure", icon: Server },
];

const CRITICITES = [
  { id: "faible", label: "Faible", color: "#71717a" },
  { id: "moyenne", label: "Moyenne", color: "#378ADD" },
  { id: "haute", label: "Haute", color: "#EF9F27" },
  { id: "critique", label: "Critique", color: "#E24B4A" },
];

const REL_TYPES = [
  { id: "rend_service_a", label: "Rend service à", color: "#1D9E75" },
  { id: "depend_de", label: "Dépend de", color: "#378ADD" },
  { id: "supporte", label: "Supporte", color: "#7F77DD" },
  { id: "controle", label: "Contrôle", color: "#EF9F27" },
  { id: "finance", label: "Finance", color: "#E24B4A" },
];

function critColor(c) { return CRITICITES.find((x) => x.id === c)?.color || "#71717a"; }
function relColor(t) { return REL_TYPES.find((x) => x.id === t)?.color || "#378ADD"; }
function actorTypeIcon(t) { return (ACTOR_TYPES.find((x) => x.id === t) || ACTOR_TYPES[1]).icon; }

// --- Custom node ---
function ActorNode({ data, selected }) {
  const Icon = actorTypeIcon(data.type);
  const col = critColor(data.criticite);
  return (
    <div
      className="rounded-lg border-2 bg-[#0a0a0a]/90 backdrop-blur px-3 py-2 min-w-[180px] shadow-lg transition-all duration-300"
      style={{
        borderColor: selected ? "#EF9F27" : data.highlighted ? "#22D3EE" : `${col}80`,
        boxShadow: data.highlighted ? "0 0 0 3px rgba(34,211,238,0.35), 0 0 24px rgba(34,211,238,0.25)" : undefined,
        opacity: data.dimmed ? 0.28 : 1,
      }}
      data-testid={`um-node-${data.id}`}
    >
      <Handle type="target" position={Position.Top} style={{ background: col }} />
      <div className="flex items-start gap-2">
        <div className="w-7 h-7 rounded-md flex items-center justify-center shrink-0" style={{ background: `${col}25`, border: `1px solid ${col}55` }}>
          <Icon className="w-3.5 h-3.5" style={{ color: col }} />
        </div>
        <div className="min-w-0">
          <div className="text-xs font-medium text-white truncate">{data.name}</div>
          {data.role && <div className="text-[10px] text-zinc-500 truncate">{data.role}</div>}
        </div>
      </div>
      <div className="flex items-center gap-1 mt-2 flex-wrap">
        <span className="text-[9px] px-1.5 py-0.5 rounded uppercase tracking-wider" style={{ background: `${col}1A`, color: col }}>
          {data.criticite}
        </span>
        {data.is_bottleneck && (
          <span className="text-[9px] px-1.5 py-0.5 rounded uppercase tracking-wider bg-[#EF9F27]/15 text-[#EF9F27] border border-[#EF9F27]/30 inline-flex items-center gap-0.5">
            <Zap className="w-2 h-2" /> Bottleneck
          </span>
        )}
        <span className="text-[9px] text-zinc-500 ml-auto">{data.centrality}%</span>
      </div>
      <Handle type="source" position={Position.Bottom} style={{ background: col }} />
    </div>
  );
}

const nodeTypes = { actor: ActorNode };

// Acteurs à surligner selon le terme de formule cliqué (lien concept ↔ carte).
function highlightIdsFor(term, graph) {
  if (!term || !graph) return new Set();
  const actors = graph.actors || [];
  const rels = graph.relations || [];
  const cen = graph.centrality || {};
  if (term === "asymetrie") {
    const scored = actors.filter((a) => (a.asymmetry_score || 0) > 0);
    if (!scored.length) return new Set();
    const max = Math.max(...scored.map((a) => a.asymmetry_score || 0));
    return new Set(scored.filter((a) => (a.asymmetry_score || 0) >= max * 0.6).map((a) => a.id));
  }
  if (term === "frequence") {
    const bott = actors.filter((a) => cen[a.id]?.is_bottleneck).map((a) => a.id);
    if (bott.length) return new Set(bott);
    const inc = {};
    rels.forEach((r) => { const t = r.to_id || r.target_id; inc[t] = (inc[t] || 0) + 1; });
    const vals = Object.values(inc);
    const maxIn = vals.length ? Math.max(...vals) : 0;
    return new Set(Object.keys(inc).filter((id) => inc[id] === maxIn && maxIn > 0));
  }
  if (term === "valeur" || term === "remplacement") {
    return new Set(actors.filter((a) => a.criticite === "haute" || a.criticite === "critique").map((a) => a.id));
  }
  return new Set();
}

export default function UtilityMapping() {
  const { t } = useTranslation();
  const [graph, setGraph] = useState(null);
  const [error, setError] = useState(null);
  const [selectedActor, setSelectedActor] = useState(null);
  const [highlightTerm, setHighlightTerm] = useState(null);
  const [editor, setEditor] = useState(null); // {kind: "actor"|"relation", item}
  const [analyzing, setAnalyzing] = useState(false);
  const [analysis, setAnalysis] = useState(null);
  const [brasArmeOpen, setBrasArmeOpen] = useState(false);
  const [nodes, setNodes, onNodesChange] = useNodesState([]);
  const [edges, setEdges, onEdgesChange] = useEdgesState([]);
  // P1 multi-maps — état des cartes (additif)
  const [maps, setMaps] = useState([]);
  const [activeMapId, setActiveMapId] = useState(() => {
    try { return localStorage.getItem("um:active_map") || null; } catch { return null; }
  });
  const [mapEditor, setMapEditor] = useState(null); // {mode:"new"|"edit-doctrine"|"rename", map?, prefill?}
  // P0 fix V21.1 — Consommation des params URL prefill_opp / prefill_level
  // émis par OriginationExtensions.jsx (boutons "Cartographier" du Friction Gap modal).
  // Affiche un bandeau d'info + ouvre automatiquement le MapEditor en mode "new" prérempli.
  const [searchParams, setSearchParams] = useSearchParams();
  const [prefillBanner, setPrefillBanner] = useState(null); // {opp_id, opp_titre, opp_secteur, level}
  // V23.14.6f — Banner contextuel "Super Partenaire" depuis Annuaire
  const [superPartnerBanner, setSuperPartnerBanner] = useState(null); // {entity_id, name}
  const prefillConsumedRef = useRef(false);
  // P0 lisibilité — filtres pour cacher des types de relations
  const [hiddenRelTypes, setHiddenRelTypes] = useState(() => new Set());
  // P0 lisibilité — option pour masquer toutes les relations
  const [hideAllRelations, setHideAllRelations] = useState(false);
  const toggleRelType = (id) => {
    setHiddenRelTypes((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id); else next.add(id);
      return next;
    });
  };

  const loadMaps = async () => {
    try {
      const r = await api.get("/workspace/utility-mapping/maps");
      const items = r.data?.items || [];
      setMaps(items);
      if (!activeMapId && items.length > 0) {
        setActiveMapId(items[0].id);
        try { localStorage.setItem("um:active_map", items[0].id); } catch {}
      } else if (activeMapId && !items.find((m) => m.id === activeMapId) && items.length > 0) {
        setActiveMapId(items[0].id);
        try { localStorage.setItem("um:active_map", items[0].id); } catch {}
      }
    } catch (e) { setError(fmtError(e)); }
  };

  const load = async () => {
    try {
      const params = activeMapId ? { map_id: activeMapId } : {};
      const r = await api.get("/workspace/utility-mapping", { params });
      setGraph(r.data);
      if (r.data?.active_map_id && r.data.active_map_id !== activeMapId) {
        setActiveMapId(r.data.active_map_id);
        try { localStorage.setItem("um:active_map", r.data.active_map_id); } catch {}
      }
    } catch (e) { setError(fmtError(e)); setGraph({ actors: [], relations: [], centrality: {} }); }
  };
  useEffect(() => { loadMaps(); }, []);
  useEffect(() => { if (activeMapId !== null) load(); /* eslint-disable-next-line */ }, [activeMapId]);

  // P0 fix V21.1 — Lecture des params URL au mount (une seule fois) :
  //   ?prefill_opp=<id>     → fetch l'opp via /workspace/origination/pipeline,
  //                            affiche bandeau + ouvre MapEditor "new" préremplie.
  //   ?prefill_level=<N>    → affiche juste un bandeau d'info (sans modal).
  // Les params sont ensuite nettoyés (setSearchParams({})) pour éviter le re-déclenchement.
  useEffect(() => {
    if (prefillConsumedRef.current) return;
    const prefillOpp = searchParams.get("prefill_opp");
    const prefillLevel = searchParams.get("prefill_level");
    const focusEntity = searchParams.get("focus_entity");
    const focusName = searchParams.get("focus_name");
    if (!prefillOpp && !prefillLevel && !focusEntity) return;
    prefillConsumedRef.current = true;

    (async () => {
      let banner = null;
      // V23.14.6f — Focus depuis Annuaire Super Partenaire : on affiche un
      // banner contextuel et on prépare un MapEditor pré-rempli avec ce
      // partenaire en acteur central, pour cartographier SES dépendances.
      if (focusEntity) {
        const name = focusName || "Super Partenaire";
        setSuperPartnerBanner({ entity_id: focusEntity, name });
        setMapEditor({
          mode: "new",
          prefill: {
            name: `Dépendances de ${name}`,
            description: `Cartographie des dépendances de ${name} (Super Partenaire) — acteurs, systèmes, processus dont il dépend, pour identifier les positions de capture de valeur à exploiter.`,
            central_actor: name,
            central_actor_type: "organisation",
            sector: "",
          },
        });
        notify.success(`Cartographie des dépendances de ${name} — identifiez sa chaîne de valeur.`);
        setSearchParams({}, { replace: true });
        return;
      }
      if (prefillOpp) {
        try {
          const r = await api.get("/workspace/origination/pipeline");
          const allOpps = Object.values(r.data?.by_step || {}).flat();
          const opp = allOpps.find((o) => o.id === prefillOpp);
          if (opp) {
            banner = {
              opp_id: opp.id,
              opp_titre: opp.titre,
              opp_secteur: opp.secteur || "",
            };
            // Ouvre automatiquement le MapEditor "Nouvelle carte" préremplie.
            setMapEditor({
              mode: "new",
              prefill: {
                name: `Cartographie — ${opp.titre}`,
                description: `Cartographie d'utilités générée depuis l'opportunité « ${opp.titre} »${opp.secteur ? ` (secteur ${opp.secteur})` : ""}.`,
                central_actor: opp.titre,
                central_actor_type: "organisation",
                sector: opp.secteur || "",
              },
            });
            notify.success(`Opportunité « ${opp.titre} » reçue depuis le Friction Gap.`);
          } else {
            banner = { opp_id: prefillOpp, opp_titre: "Opportunité introuvable", opp_secteur: "" };
            notify.error("Opportunité liée introuvable — cartographie générique.");
          }
        } catch (e) {
          banner = { opp_id: prefillOpp, opp_titre: "Erreur de chargement", opp_secteur: "" };
        }
      } else if (prefillLevel) {
        banner = { level: prefillLevel };
        notify.success(`Cartographie demandée pour le niveau de gravité ${prefillLevel}.`);
      }
      if (banner) setPrefillBanner(banner);
      // Nettoie l'URL pour ne pas re-déclencher au refresh.
      setSearchParams({}, { replace: true });
    })();
  }, [searchParams, setSearchParams]);

  const selectMap = (id) => {
    if (id === activeMapId) return;
    setActiveMapId(id);
    try { localStorage.setItem("um:active_map", id); } catch {}
    setSelectedActor(null);
    setAnalysis(null);
  };

  // Sync graph → react-flow nodes/edges
  useEffect(() => {
    if (!graph) return;
    const c = graph.centrality || {};
    const hi = highlightIdsFor(highlightTerm, graph);
    setNodes(graph.actors.map((a) => ({
      id: a.id, type: "actor",
      position: { x: a.position_x || 400, y: a.position_y || 300 },
      data: {
        ...a, centrality: c[a.id]?.centrality ?? 0, is_bottleneck: c[a.id]?.is_bottleneck,
        highlighted: hi.has(a.id),
        dimmed: !!highlightTerm && !hi.has(a.id),
      },
    })));
    const visibleRelations = hideAllRelations
      ? []
      : graph.relations.filter((r) => !hiddenRelTypes.has(r.type));
    setEdges(visibleRelations.map((r) => ({
      id: r.id, source: r.from_id, target: r.to_id,
      label: REL_TYPES.find((x) => x.id === r.type)?.label || r.type,
      style: { stroke: relColor(r.type), strokeWidth: Math.max(1, r.force * 0.8) },
      labelStyle: { fontSize: 10, fill: "#a1a1aa" },
      labelBgStyle: { fill: "#0a0a0a", fillOpacity: 0.8 },
      markerEnd: { type: MarkerType.ArrowClosed, color: relColor(r.type) },
      data: { ...r },
    })));
  }, [graph, setNodes, setEdges, hiddenRelTypes, hideAllRelations, highlightTerm]);

  const onConnect = useCallback(async (conn) => {
    try {
      await api.post("/workspace/utility-mapping/relations", {
        from_id: conn.source, to_id: conn.target, type: "depend_de", force: 3,
      }, { params: activeMapId ? { map_id: activeMapId } : {} });
      notify.success(t("saas.utility_mapping.toast_relation_created"));
      await load();
    } catch (e) { notify.error(fmtError(e)); }
  }, [t, activeMapId]);

  const onNodeDragStop = useCallback(async (_evt, node) => {
    try {
      await api.patch(`/workspace/utility-mapping/actors/${node.id}`, {
        position_x: node.position.x, position_y: node.position.y,
      });
    } catch { /* silent — UX */ }
  }, []);

  const onNodeClick = useCallback((_evt, node) => {
    const actor = (graph?.actors || []).find((a) => a.id === node.id);
    setSelectedActor(actor || null);
  }, [graph]);

  const onEdgeClick = useCallback(async (evt, edge) => {
    evt.stopPropagation();
    if (!window.confirm(t("saas.utility_mapping.confirm_delete_relation"))) return;
    try {
      await api.delete(`/workspace/utility-mapping/relations/${edge.id}`);
      notify.success(t("saas.utility_mapping.toast_relation_deleted"));
      await load();
    } catch (e) { notify.error(fmtError(e)); }
  }, [t]);

  const seedDemo = async () => {
    try {
      await api.post("/workspace/utility-mapping/seed-demo", null, { params: activeMapId ? { map_id: activeMapId } : {} });
      notify.success(t("saas.utility_mapping.toast_seeded"));
      await load();
      await loadMaps();
    } catch (e) { notify.error(fmtError(e)); }
  };

  const removeActor = async (a) => {
    if (!window.confirm(t("saas.utility_mapping.confirm_delete_actor"))) return;
    try {
      await api.delete(`/workspace/utility-mapping/actors/${a.id}`);
      notify.success(t("saas.utility_mapping.toast_deleted"));
      setSelectedActor(null);
      await load();
    } catch (e) { notify.error(fmtError(e)); }
  };

  const analyzeSystem = async () => {
    setAnalyzing(true);
    try {
      const r = await api.post("/workspace/utility-mapping/analyze", null, { params: activeMapId ? { map_id: activeMapId } : {} });
      setAnalysis(r.data);
      notify.success(t("saas.utility_mapping.toast_analyzed"));
    } catch (e) { notify.error(fmtError(e)); }
    finally { setAnalyzing(false); }
  };

  const isEmpty = graph && graph.actors.length === 0;
  const activeMap = maps.find((m) => m.id === activeMapId);

  return (
    <div className="space-y-4 flex flex-col h-[calc(100vh-120px)]" data-testid="um-root" id="um-capture-root">
      <ThreeQPageHeader endpoint="/workspace/asymetries/top-priority" />
      <header className="flex items-end justify-between flex-wrap gap-3 shrink-0">
        <div className="min-w-0">
          <div className="flex items-center gap-2 text-[#1D9E75] noam-overline">
            <Network className="w-3 h-3" /> {t("saas.utility_mapping.overline")}
          </div>
          <h1 className="noam-display text-2xl font-bold tracking-tight mt-1">{t("saas.utility_mapping.title")}</h1>
          <p className="text-zinc-400 text-xs mt-1 max-w-3xl">
            {t("saas.utility_mapping.subtitle")}
          </p>
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          <PitchCaptureButton
            targetId="um-capture-root"
            filename="noam-cartographie-dependances"
            docTitle="Cartographie des dépendances"
            docSubtitle="Qui sert qui, qui dépend de qui — goulots d'étranglement et asymétries monétisables de votre écosystème."
            threeQEndpoint="/workspace/asymetries/top-priority"
          />
          <button onClick={analyzeSystem} disabled={analyzing || isEmpty} className="btn-ghost text-xs disabled:opacity-50" data-testid="um-analyze-btn">
            {analyzing ? <><Loader2 className="w-3.5 h-3.5 animate-spin" /> {t("saas.utility_mapping.analyzing")}</> : <><Sparkles className="w-3.5 h-3.5" /> {t("saas.utility_mapping.analyze_btn")}</>}
          </button>
          <button onClick={() => setBrasArmeOpen(true)} className="btn-secondary text-xs" data-testid="um-bras-arme-btn" title="Identifier les grands acteurs compatibles">
            <Crown className="w-3.5 h-3.5 text-[#EFD127]" /> {t("bras_arme.button", { defaultValue: "Super Partenaires & Clients" })}
          </button>
          <button onClick={() => setEditor({ kind: "actor", item: null })} className="btn-primary text-xs" data-testid="um-create-actor-btn">
            <Plus className="w-3.5 h-3.5" /> {t("saas.utility_mapping.add_actor_btn")}
          </button>
        </div>
      </header>

      <BrasArmeAnalysisPanel open={brasArmeOpen} onClose={() => setBrasArmeOpen(false)} sourceModule="asymetries" />

      {/* P1 multi-maps — Bandeau de sélection des cartes */}
      <MapSwitcher
        maps={maps}
        activeMapId={activeMapId}
        onSelect={selectMap}
        onNew={() => setMapEditor({ mode: "new" })}
        onRename={(m) => setMapEditor({ mode: "rename", map: m })}
        onEditDoctrine={(m) => setMapEditor({ mode: "edit-doctrine", map: m })}
        onDelete={async (m) => {
          if (!window.confirm(`Supprimer la carte « ${m.name} » et tous ses acteurs ?`)) return;
          try {
            // P0 UX — Bascule sur une autre carte AVANT de supprimer
            // pour éviter le bandeau transitoire "Carte introuvable"
            const other = (maps || []).find((x) => x.id !== m.id);
            if (other) {
              setActiveMapId(other.id);
              try { localStorage.setItem("um:active_map", other.id); } catch {}
              setError(null);
              setSelectedActor(null);
              setAnalysis(null);
            }
            await api.delete(`/workspace/utility-mapping/maps/${m.id}`);
            notify.success("Carte supprimée.");
            await loadMaps();
            if (other) await load();
          } catch (e) { notify.error(fmtError(e)); }
        }}
      />

      {error && (
        <div className="flex items-start gap-2 p-3 rounded-md border border-[#E24B4A]/30 bg-[#E24B4A]/10 text-[#FCA5A5] text-xs">
          <AlertTriangle className="w-3.5 h-3.5 mt-0.5" /><span>{error}</span>
          <button className="ml-auto" onClick={() => setError(null)}><X className="w-3 h-3" /></button>
        </div>
      )}

      {/* P0 fix V21.1 — Bandeau de contexte Friction Gap → Cartographie */}
      {prefillBanner && (
        <div
          className="flex items-start gap-3 p-3 rounded-md border border-[#1D9E75]/30 bg-[#1D9E75]/10 text-zinc-200 text-xs shrink-0"
          data-testid="um-prefill-banner"
        >
          <Link2 className="w-4 h-4 mt-0.5 text-[#1D9E75] shrink-0" />
          <div className="flex-1 min-w-0">
            {prefillBanner.opp_titre ? (
              <>
                <div className="font-medium text-white">
                  Cartographie liée à l'opportunité « {prefillBanner.opp_titre} »
                </div>
                <div className="text-zinc-400 text-[11px] mt-0.5">
                  {prefillBanner.opp_secteur && <>Secteur : <span className="text-zinc-300">{prefillBanner.opp_secteur}</span> · </>}
                  Le modal « Nouvelle carte » a été prérempli avec cet acteur central. Sélectionnez le mode IA et cliquez « Générer ».
                </div>
              </>
            ) : (
              <>
                <div className="font-medium text-white">
                  Cartographie demandée pour le niveau de gravité {prefillBanner.level}
                </div>
                <div className="text-zinc-400 text-[11px] mt-0.5">
                  Sélectionnez une carte existante ou créez-en une nouvelle pour ce niveau.
                </div>
              </>
            )}
          </div>
          <button
            onClick={() => setPrefillBanner(null)}
            className="p-1 rounded hover:bg-white/5 text-zinc-500"
            data-testid="um-prefill-banner-close"
            title="Fermer"
          >
            <X className="w-3.5 h-3.5" />
          </button>
        </div>
      )}

      {/* V23.14.6f — Bandeau de contexte Super Partenaire → Cartographie des dépendances */}
      {superPartnerBanner && (
        <div
          className="flex items-start gap-3 p-3 rounded-md border border-[#EFD127]/30 bg-gradient-to-r from-[#EFD127]/[0.10] to-transparent text-zinc-200 text-xs shrink-0"
          data-testid="um-super-partner-banner"
        >
          <Crown className="w-4 h-4 mt-0.5 text-[#EFD127] shrink-0" />
          <div className="flex-1 min-w-0">
            <div className="font-medium text-white">
              Cartographie des dépendances de <span className="text-[#EFD127]">{superPartnerBanner.name}</span> (Super Partenaire)
            </div>
            <div className="text-zinc-400 text-[11px] mt-0.5">
              Identifiez les acteurs, systèmes, processus dont <span className="text-zinc-300">{superPartnerBanner.name}</span> dépend — c'est là que vous trouverez vos positions de capture de valeur (amont · aval · soutien du soutien) pour devenir son bras opérationnel.
            </div>
          </div>
          <button
            onClick={() => setSuperPartnerBanner(null)}
            className="p-1 rounded hover:bg-white/5 text-zinc-500"
            data-testid="um-super-partner-banner-close"
            title="Fermer"
          >
            <X className="w-3.5 h-3.5" />
          </button>
        </div>
      )}

      {analysis && <AnalysisCard analysis={analysis} actors={graph?.actors || []} onClose={() => setAnalysis(null)} />}

      {isEmpty ? (
        <div className="noam-card p-10 text-center flex-1" data-testid="um-empty">
          <div className="w-14 h-14 mx-auto rounded-xl bg-[#1D9E75]/10 border border-[#1D9E75]/30 flex items-center justify-center">
            <Network className="w-6 h-6 text-[#1D9E75]" />
          </div>
          <h2 className="noam-display text-xl font-semibold mt-4">{t("saas.utility_mapping.empty_title")}</h2>
          <p className="text-zinc-400 text-sm mt-2 max-w-md mx-auto">
            Choisissez un <strong className="text-zinc-200">acteur central</strong> et laissez l'IA hybride (NOAM + Tavily) générer automatiquement l'écosystème de dépendances autour de lui. Ou créez manuellement vos acteurs.
          </p>
          <div className="mt-6 flex items-center justify-center gap-2 flex-wrap">
            <button onClick={() => setMapEditor({ mode: "new" })} className="btn-primary text-sm" data-testid="um-generate-btn">
              <Sparkles className="w-4 h-4" /> Générer un écosystème par IA
            </button>
            <button onClick={seedDemo} className="btn-ghost text-sm" data-testid="um-seed-btn">
              <Sparkles className="w-4 h-4" /> {t("saas.utility_mapping.seed_btn")}
            </button>
            <button onClick={() => setEditor({ kind: "actor", item: null })} className="btn-ghost text-sm">
              <Plus className="w-4 h-4" /> {t("saas.utility_mapping.create_first")}
            </button>
          </div>
        </div>
      ) : (
        <div className="grid grid-cols-12 gap-3 flex-1 min-h-[500px]">
          <div className="col-span-12 lg:col-span-9 noam-card p-0 overflow-hidden" data-testid="um-canvas">
            <div style={{ width: "100%", height: "100%" }}>
              <ReactFlow
                nodes={nodes} edges={edges}
                onNodesChange={onNodesChange} onEdgesChange={onEdgesChange}
                onConnect={onConnect} onNodeDragStop={onNodeDragStop}
                onNodeClick={onNodeClick} onEdgeClick={onEdgeClick}
                nodeTypes={nodeTypes}
                fitView fitViewOptions={{ padding: 0.2 }}
                proOptions={{ hideAttribution: true }}
                colorMode="dark"
              >
                <Background color="#27272a" gap={20} />
                <Controls className="!bg-[#0a0a0a] !border-white/10" />
                <MiniMap nodeColor={(n) => critColor(n.data?.criticite)} pannable zoomable className="!bg-[#0a0a0a] !border !border-white/10" />
              </ReactFlow>
            </div>
          </div>
          <aside className="col-span-12 lg:col-span-3 space-y-3 overflow-y-auto pr-1">
            <AsymmetryDoctrine
              catalog={graph?.asymmetry_catalog}
              topMonetized={graph?.top_monetized}
              typeDistribution={graph?.type_distribution}
              positionDistribution={graph?.position_distribution}
              activeMap={activeMap}
              onEditDoctrine={() => activeMap && setMapEditor({ mode: "edit-doctrine", map: activeMap })}
            />
            {/* V23.12 — Pédagogie asymétries + analyse "si X disparaît" + liens cross-modules */}
            <AsymmetryEducationPanel
              selectedActor={selectedActor}
              actors={graph?.actors || []}
              relations={graph?.relations || []}
              onHighlightTerm={setHighlightTerm}
            />
            <KeyQuestions />
            <Legend
              hiddenRelTypes={hiddenRelTypes}
              hideAllRelations={hideAllRelations}
              onToggleRelType={toggleRelType}
              onToggleAllRelations={() => setHideAllRelations((v) => !v)}
              onResetFilters={() => { setHiddenRelTypes(new Set()); setHideAllRelations(false); }}
              totalRelations={graph?.relations?.length || 0}
              visibleRelations={hideAllRelations ? 0 : (graph?.relations?.filter((r) => !hiddenRelTypes.has(r.type)).length || 0)}
            />
            {selectedActor ? (
              <ActorDetailPanel actor={selectedActor} centrality={graph?.centrality?.[selectedActor.id]}
                onEdit={() => setEditor({ kind: "actor", item: selectedActor })}
                onDelete={() => removeActor(selectedActor)}
                onClose={() => setSelectedActor(null)} />
            ) : (
              <div className="noam-card p-4 text-xs text-zinc-500 italic">
                {t("saas.utility_mapping.click_node")}
              </div>
            )}
          </aside>
        </div>
      )}

      {editor && (
        <ActorEditor actor={editor.item} onClose={() => setEditor(null)} activeMapId={activeMapId}
          onSaved={async () => { setEditor(null); await load(); }} />
      )}

      {mapEditor && (
        <MapEditor
          mode={mapEditor.mode}
          map={mapEditor.map}
          prefill={mapEditor.prefill}
          activeMapId={activeMapId}
          onClose={() => setMapEditor(null)}
          onSaved={async (newId) => {
            setMapEditor(null);
            await loadMaps();
            if (newId) selectMap(newId);
            else await load();
          }}
        />
      )}
    </div>
  );
}

function KeyQuestions() {
  const { t } = useTranslation();
  return (
    <div className="noam-card p-4" data-testid="um-key-questions">
      <div className="noam-overline mb-2">Diagnostic fractal · 5 questions</div>
      <ul className="space-y-1.5 text-[11px] text-zinc-300">
        <li className="flex items-start gap-1.5"><span className="w-1 h-1 mt-1.5 rounded-full bg-[#378ADD] shrink-0" /> Qui dépend de moi ?</li>
        <li className="flex items-start gap-1.5"><span className="w-1 h-1 mt-1.5 rounded-full bg-[#1D9E75] shrink-0" /> De qui je dépends ?</li>
        <li className="flex items-start gap-1.5"><span className="w-1 h-1 mt-1.5 rounded-full bg-[#EF9F27] shrink-0" /> Que se passe-t-il si je disparais ?</li>
        <li className="flex items-start gap-1.5"><span className="w-1 h-1 mt-1.5 rounded-full bg-[#C26EE7] shrink-0" /> Que se passe-t-il si eux disparaissent ?</li>
        <li className="flex items-start gap-1.5"><span className="w-1 h-1 mt-1.5 rounded-full bg-[#E24B4A] shrink-0" /> Où je capte la valeur — <strong>amont, aval ou soutien</strong> — et pourquoi je suis non-remplaçable là ?</li>
      </ul>
    </div>
  );
}

function AsymmetryDoctrine({ catalog, topMonetized, typeDistribution, positionDistribution, activeMap, onEditDoctrine }) {
  const types = catalog || ASYM_TYPES;
  const totalTagged = Object.values(typeDistribution || {}).reduce((a, b) => a + b, 0);
  const totalPositioned = Object.values(positionDistribution || {}).reduce((a, b) => a + b, 0);
  const customText = (activeMap?.doctrine_text || "").trim();
  return (
    <div className="noam-card p-4 space-y-3" data-testid="um-asymmetry-doctrine">
      <div className="flex items-center gap-2">
        <Crown className="w-3 h-3 text-[#EF9F27]" />
        <div className="noam-overline flex-1">Doctrine fractale — Asymétries monétisables</div>
        {onEditDoctrine && (
          <button
            type="button"
            onClick={onEditDoctrine}
            className="p-1 rounded hover:bg-white/5 text-zinc-500 hover:text-[#EF9F27]"
            title="Éditer le texte de doctrine de cette carte"
            data-testid="um-doctrine-edit-btn"
          >
            <Edit2 className="w-3 h-3" />
          </button>
        )}
      </div>
      {customText ? (
        <p className="text-[11px] text-zinc-300 leading-relaxed whitespace-pre-wrap">{customText}</p>
      ) : (
        <p className="text-[11px] text-zinc-400 leading-relaxed">
          La rente n'a pas de position unique. Elle naît partout où <strong className="text-zinc-200">une asymétrie devient non-remplaçable</strong> — qu'on soit
          en <span className="text-[#1D9E75]">amont</span> (on contrôle l'entrée), en <span className="text-[#378ADD]">aval</span> (on contrôle la sortie),
          ou qu'on <span className="text-[#EF9F27]">soutienne le soutien</span> (on est l'infrastructure invisible d'un intermédiaire).
        </p>
      )}
      <p className="text-[10px] text-zinc-500 italic">
        Score = Asymétrie × Fréquence × Valeur × Difficulté de remplacement — indépendamment de la position dans la chaîne.
      </p>

      {totalPositioned > 0 && (
        <div className="grid grid-cols-3 gap-1" data-testid="um-position-distribution">
          {Object.entries(POSITION_CAPTURE).map(([k, m]) => {
            const Icon = m.icon;
            const n = positionDistribution?.[k] || 0;
            return (
              <div key={k} className="border border-white/5 rounded p-1.5 text-center" title={m.desc}>
                <Icon className="w-3 h-3 mx-auto" style={{ color: m.color }} />
                <div className="text-[9px] text-zinc-400 uppercase tracking-wider mt-0.5">{m.label}</div>
                <div className="text-[11px] font-bold" style={{ color: m.color }}>{n}</div>
              </div>
            );
          })}
        </div>
      )}

      {totalTagged > 0 && (
        <div>
          <div className="text-[9px] uppercase tracking-wider text-zinc-500 mb-1.5">Distribution ({totalTagged} acteurs étiquetés)</div>
          <div className="grid grid-cols-2 gap-1">
            {Object.entries(typeDistribution || {}).filter(([, n]) => n > 0).map(([k, n]) => {
              const meta = ASYM_TYPES[k] || { label: k, icon: Network, color: "#378ADD" };
              const Icon = meta.icon;
              return (
                <div key={k} className="flex items-center gap-1 text-[10px]" style={{ color: meta.color }}>
                  <Icon className="w-2.5 h-2.5" />
                  <span className="text-zinc-300">{meta.label}</span>
                  <span className="ml-auto text-zinc-500">{n}</span>
                </div>
              );
            })}
          </div>
        </div>
      )}

      {topMonetized?.length > 0 && (
        <div className="pt-2 border-t border-white/5">
          <div className="text-[9px] uppercase tracking-wider text-zinc-500 mb-1.5">Top 5 captivité (toutes positions)</div>
          <ul className="space-y-1.5">
            {topMonetized.map((m, i) => {
              const meta = m.asymmetry_type ? ASYM_TYPES[m.asymmetry_type] : null;
              const posMeta = m.position_capture ? POSITION_CAPTURE[m.position_capture] : null;
              const bandColor = m.asymmetry_band === "extreme" ? "#E24B4A"
                : m.asymmetry_band === "high" ? "#EF9F27"
                : m.asymmetry_band === "median" ? "#378ADD" : "#71717a";
              return (
                <li key={m.id} className="flex items-center gap-2 text-[11px]" data-testid={`um-top-monetized-${i}`}>
                  <span className="text-zinc-600 font-mono text-[10px]">#{i + 1}</span>
                  <span className="flex-1 truncate text-zinc-200" title={m.name}>{m.name}</span>
                  {posMeta && <posMeta.icon className="w-2.5 h-2.5" style={{ color: posMeta.color }} title={posMeta.label} />}
                  {meta && <meta.icon className="w-2.5 h-2.5" style={{ color: meta.color }} />}
                  <span className="text-[10px] font-bold" style={{ color: bandColor }}>{m.asymmetry_score}</span>
                </li>
              );
            })}
          </ul>
        </div>
      )}
    </div>
  );
}

function Legend({ hiddenRelTypes, hideAllRelations, onToggleRelType, onToggleAllRelations, onResetFilters, totalRelations = 0, visibleRelations = 0 }) {
  const { t } = useTranslation();
  // Si pas de toggles fournis : fallback lecture seule (rétro-compat)
  const interactive = typeof onToggleRelType === "function";
  const hidden = hiddenRelTypes || new Set();
  const filtersActive = hidden.size > 0 || hideAllRelations;
  return (
    <div className="noam-card p-4" data-testid="um-legend">
      <div className="flex items-center justify-between gap-2 mb-2">
        <div className="noam-overline">{t("saas.utility_mapping.legend")}</div>
        {interactive && totalRelations > 0 && (
          <span className="text-[9px] text-zinc-500 uppercase tracking-wider" data-testid="um-legend-counter">
            {visibleRelations}/{totalRelations}
          </span>
        )}
      </div>
      {interactive && (
        <p className="text-[10px] text-zinc-500 italic mb-2">
          Cliquez un type pour le masquer et clarifier la carte.
        </p>
      )}
      <div className="space-y-1">
        {REL_TYPES.map((r) => {
          const isHidden = hidden.has(r.id);
          const dimmed = isHidden || hideAllRelations;
          if (!interactive) {
            return (
              <div key={r.id} className="flex items-center gap-2 text-[11px]">
                <span className="w-6 h-0.5 rounded-full" style={{ background: r.color }} />
                <span className="text-zinc-300">{r.label}</span>
              </div>
            );
          }
          return (
            <button
              key={r.id}
              type="button"
              onClick={() => onToggleRelType(r.id)}
              disabled={hideAllRelations}
              className={`w-full flex items-center gap-2 text-[11px] px-1.5 py-1 rounded transition-colors ${
                dimmed ? "opacity-40" : "hover:bg-white/5"
              } ${hideAllRelations ? "cursor-not-allowed" : ""}`}
              data-testid={`um-legend-toggle-${r.id}`}
              title={isHidden ? `Afficher ${r.label}` : `Masquer ${r.label}`}
            >
              <span className={`w-6 h-0.5 rounded-full transition-opacity ${dimmed ? "opacity-30" : ""}`} style={{ background: r.color }} />
              <span className={`flex-1 text-left ${dimmed ? "text-zinc-500 line-through" : "text-zinc-300"}`}>{r.label}</span>
              {isHidden ? (
                <EyeOff className="w-3 h-3 text-zinc-600" />
              ) : (
                <Eye className="w-3 h-3 text-zinc-500" />
              )}
            </button>
          );
        })}
      </div>
      {interactive && (
        <div className="flex items-center gap-2 mt-3 pt-3 border-t border-white/5">
          <button
            type="button"
            onClick={onToggleAllRelations}
            className={`flex-1 text-[10px] uppercase tracking-wider px-2 py-1.5 rounded border transition-colors ${
              hideAllRelations
                ? "border-[#EF9F27] bg-[#EF9F27]/10 text-[#EF9F27]"
                : "border-white/10 text-zinc-400 hover:text-white hover:border-white/30"
            }`}
            data-testid="um-legend-toggle-all"
            title="Masquer toutes les flèches pour ne voir que les acteurs"
          >
            {hideAllRelations ? "Tout afficher" : "Masquer toutes les flèches"}
          </button>
          {filtersActive && (
            <button
              type="button"
              onClick={onResetFilters}
              className="text-[10px] text-zinc-400 hover:text-white px-2 py-1.5"
              data-testid="um-legend-reset"
              title="Réinitialiser les filtres"
            >
              Réinitialiser
            </button>
          )}
        </div>
      )}
    </div>
  );
}

function ActorDetailPanel({ actor, centrality, onEdit, onDelete, onClose }) {
  const col = critColor(actor.criticite);
  return (
    <div className="noam-card p-4" data-testid="um-actor-detail">
      <div className="flex items-start justify-between gap-2 mb-2">
        <div className="min-w-0">
          <div className="text-sm font-semibold text-white truncate">{actor.name}</div>
          {actor.role && <div className="text-[11px] text-zinc-400">{actor.role}</div>}
        </div>
        <button onClick={onClose} className="p-1 rounded hover:bg-white/5"><X className="w-3.5 h-3.5" /></button>
      </div>
      <div className="flex items-center gap-1 mb-3 flex-wrap">
        <span className="text-[10px] px-2 py-0.5 rounded uppercase tracking-wider" style={{ background: `${col}1A`, color: col, border: `1px solid ${col}40` }}>
          {actor.criticite}
        </span>
        <span className="text-[10px] px-2 py-0.5 rounded bg-white/5 border border-white/10 text-zinc-300 capitalize">{actor.type}</span>
      </div>
      {actor.description && <p className="text-xs text-zinc-300 leading-relaxed mb-3">{actor.description}</p>}
      {centrality && (
        <div className="grid grid-cols-2 gap-2 mb-3 text-[11px]">
          <div className="bg-white/[0.03] border border-white/5 rounded p-2">
            <div className="text-zinc-500 uppercase tracking-wider text-[9px]">Dépendances</div>
            <div className="text-zinc-200 mt-1">
              {centrality.in_degree} entrant{centrality.in_degree > 1 ? "es" : "e"} · {centrality.out_degree} sortant{centrality.out_degree > 1 ? "es" : "e"}
            </div>
          </div>
          <div className="bg-white/[0.03] border border-white/5 rounded p-2">
            <div className="text-zinc-500 uppercase tracking-wider text-[9px]">Centralité</div>
            <div className="text-zinc-200 mt-1">{centrality.centrality}% {centrality.is_bottleneck && "· Bottleneck"}</div>
          </div>
        </div>
      )}

      {/* P0 — Doctrine d'asymétrie monétisée */}
      {centrality && (centrality.asymmetry_score || centrality.asymmetry_type || actor.position_capture) && (
        <div className="mb-3 p-2.5 rounded-md border border-[#EF9F27]/30 bg-gradient-to-br from-[#EF9F27]/5 to-transparent" data-testid="um-detail-asymmetry">
          <div className="flex items-center justify-between gap-2 mb-1.5">
            <div className="flex items-center gap-1.5">
              <Crown className="w-3 h-3 text-[#EF9F27]" />
              <span className="text-[10px] uppercase tracking-wider text-[#EF9F27] font-semibold">Asymétrie monétisée</span>
            </div>
            <span className="text-lg font-bold text-[#EF9F27]">{centrality.asymmetry_score || 0}</span>
          </div>
          {centrality.asymmetry_type && ASYM_TYPES[centrality.asymmetry_type] && (() => {
            const meta = ASYM_TYPES[centrality.asymmetry_type];
            const Icon = meta.icon;
            return (
              <div className="text-[10px]" style={{ color: meta.color }}>
                <div className="flex items-center gap-1.5">
                  <Icon className="w-2.5 h-2.5" />
                  <span>{meta.label}</span>
                </div>
                <p className="text-zinc-400 leading-relaxed mt-0.5">{meta.explanation}</p>
              </div>
            );
          })()}
          {actor.position_capture && POSITION_CAPTURE[actor.position_capture] && (() => {
            const pm = POSITION_CAPTURE[actor.position_capture];
            const PIcon = pm.icon;
            return (
              <div className="flex items-center gap-1.5 text-[10px] mt-1" style={{ color: pm.color }} data-testid="um-detail-position">
                <PIcon className="w-2.5 h-2.5" />
                <span>Position : {pm.label}</span>
              </div>
            );
          })()}
          <div className="text-[10px] text-zinc-500 mt-1">
            Bande : <span className="uppercase">{centrality.asymmetry_band}</span>
          </div>
        </div>
      )}

      {/* Réponses au diagnostic Bocchio si présentes */}
      {(actor.dx_who_depends || actor.dx_asymmetry_monetized) && (
        <div className="mb-3 space-y-1.5 text-[11px]">
          {actor.dx_who_depends && <div><span className="text-zinc-500 font-semibold">Qui en dépend :</span> <span className="text-zinc-300">{actor.dx_who_depends}</span></div>}
          {actor.dx_who_i_depend_on && <div><span className="text-zinc-500 font-semibold">Dépend de :</span> <span className="text-zinc-300">{actor.dx_who_i_depend_on}</span></div>}
          {actor.dx_asymmetry_monetized && <div><span className="text-[#EF9F27] font-semibold">Monétisation :</span> <span className="text-zinc-200">{actor.dx_asymmetry_monetized}</span></div>}
        </div>
      )}
      <div className="flex items-center gap-2">
        <button onClick={onEdit} className="btn-ghost text-[11px] flex-1" data-testid="um-detail-edit"><Edit2 className="w-3 h-3" /> Modifier</button>
        <button onClick={onDelete} className="text-[11px] text-zinc-500 hover:text-[#E24B4A] px-2 py-1.5 rounded hover:bg-white/5 inline-flex items-center gap-1" data-testid="um-detail-delete">
          <Trash2 className="w-3 h-3" /> Suppr.
        </button>
      </div>
    </div>
  );
}

function AnalysisCard({ analysis, actors, onClose }) {
  const findName = (id) => actors.find((a) => a.id === id)?.name || id;
  return (
    <div className="noam-card p-5" data-testid="um-analysis-card">
      <div className="flex items-start justify-between mb-3">
        <div>
          <div className="noam-overline text-[#7F77DD]">Analyse NOAM — lecture systémique</div>
          <div className="noam-display text-base font-semibold mt-1">Lecture du graphe</div>
        </div>
        <button onClick={onClose} className="p-1.5 rounded hover:bg-white/5"><X className="w-4 h-4" /></button>
      </div>
      {analysis.synthese && <p className="text-sm text-zinc-200 leading-relaxed mb-4">{analysis.synthese}</p>}
      <div className="grid md:grid-cols-2 gap-3">
        <AnalysisColumn label="Acteurs critiques" items={analysis.acteurs_critiques} color="#E24B4A" findName={findName} useActorId />
        <AnalysisColumn label="Points de défaillance unique" items={analysis.points_defaillance_unique} color="#EF9F27" findName={findName} useActorId />
        <AnalysisColumn label="Dépendances dangereuses" items={analysis.dependances_dangereuses} color="#378ADD" />
        <AnalysisColumn label="Zones de captation de valeur" items={analysis.zones_captation_valeur} color="#1D9E75" />
      </div>
      {analysis.recommandations?.length > 0 && (
        <div className="mt-4 pt-3 border-t border-white/5">
          <div className="text-[10px] uppercase tracking-wider text-zinc-500 mb-1.5">Recommandations</div>
          <ul className="space-y-1">
            {analysis.recommandations.map((r, i) => (
              <li key={i} className="text-xs text-zinc-300 flex items-start gap-2">
                <span className="w-1 h-1 rounded-full mt-1.5 shrink-0 bg-[#7F77DD]" /> <span>{r}</span>
              </li>
            ))}
          </ul>
        </div>
      )}
    </div>
  );
}

function AnalysisColumn({ label, items, color, findName, useActorId }) {
  if (!items || items.length === 0) return null;
  return (
    <div>
      <div className="text-[10px] uppercase tracking-wider mb-1.5" style={{ color }}>{label}</div>
      <ul className="space-y-2">
        {items.map((it, i) => (
          <li key={i} className="text-xs">
            {useActorId && it.actor_id && (
              <div className="text-zinc-200 font-medium">{findName(it.actor_id)}</div>
            )}
            <div className={`text-zinc-${useActorId ? "500" : "300"} text-[11px] ${useActorId ? "mt-0.5" : ""}`}>{it.raison}</div>
          </li>
        ))}
      </ul>
    </div>
  );
}

function ActorEditor({ actor, onClose, onSaved, activeMapId }) {
  const isEdit = !!actor;
  const [form, setForm] = useState(() => actor || {
    name: "", type: "organisation", role: "", criticite: "moyenne", description: "",
    asymmetry_type: "", position_capture: "",
    dx_who_depends: "", dx_who_i_depend_on: "",
    dx_if_i_disappear: "", dx_if_they_disappear: "", dx_asymmetry_monetized: "",
  });
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");
  const [showDx, setShowDx] = useState(!!actor && !!(actor.dx_who_depends || actor.dx_asymmetry_monetized));

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true); setErr("");
    try {
      const payload = {
        name: form.name.trim(),
        type: form.type, role: (form.role || "").trim(),
        criticite: form.criticite, description: (form.description || "").trim(),
        asymmetry_type: form.asymmetry_type || null,
        position_capture: form.position_capture || null,
        dx_who_depends: form.dx_who_depends || "",
        dx_who_i_depend_on: form.dx_who_i_depend_on || "",
        dx_if_i_disappear: form.dx_if_i_disappear || "",
        dx_if_they_disappear: form.dx_if_they_disappear || "",
        dx_asymmetry_monetized: form.dx_asymmetry_monetized || "",
      };
      if (isEdit) {
        await api.patch(`/workspace/utility-mapping/actors/${actor.id}`, payload);
        notify.success("Acteur mis à jour.");
      } else {
        await api.post("/workspace/utility-mapping/actors", { ...payload, position_x: 400, position_y: 300 }, { params: activeMapId ? { map_id: activeMapId } : {} });
        notify.success("Acteur créé.");
      }
      onSaved();
    } catch (e) { setErr(fmtError(e)); }
    finally { setBusy(false); }
  };

  return (
    <div className="fixed inset-0 z-50 bg-black/70 backdrop-blur flex items-center justify-center p-4 overflow-y-auto" onClick={onClose}>
      <div className="noam-card w-full max-w-2xl my-8" onClick={(e) => e.stopPropagation()} data-testid="um-editor-modal">
        <div className="flex items-start justify-between p-5 border-b border-white/5">
          <div>
            <div className="noam-overline">{isEdit ? "Modifier l'acteur" : "Nouvel acteur"}</div>
            <div className="noam-display text-base font-semibold mt-0.5">{isEdit ? actor.name : "Cartographier"}</div>
          </div>
          <button onClick={onClose} className="p-1.5 rounded hover:bg-white/5"><X className="w-4 h-4" /></button>
        </div>
        <form onSubmit={submit} className="p-5 space-y-4 max-h-[75vh] overflow-y-auto">
          <div>
            <label className="text-[11px] text-zinc-400 uppercase tracking-wider mb-1 block">Nom *</label>
            <input required autoFocus value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })}
              placeholder="Ex : Banque Privée Exemple SA" className="noam-input" data-testid="um-form-name" />
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="text-[11px] text-zinc-400 uppercase tracking-wider mb-1 block">Type</label>
              <select value={form.type} onChange={(e) => setForm({ ...form, type: e.target.value })} className="noam-input" data-testid="um-form-type">
                {ACTOR_TYPES.map((t) => <option key={t.id} value={t.id}>{t.label}</option>)}
              </select>
            </div>
            <div>
              <label className="text-[11px] text-zinc-400 uppercase tracking-wider mb-1 block">Criticité</label>
              <select value={form.criticite} onChange={(e) => setForm({ ...form, criticite: e.target.value })} className="noam-input">
                {CRITICITES.map((c) => <option key={c.id} value={c.id}>{c.label}</option>)}
              </select>
            </div>
          </div>
          <div>
            <label className="text-[11px] text-zinc-400 uppercase tracking-wider mb-1 block">Rôle</label>
            <input value={form.role} onChange={(e) => setForm({ ...form, role: e.target.value })}
              placeholder="Ex : Régulateur prudentiel" className="noam-input" />
          </div>
          <div>
            <label className="text-[11px] text-zinc-400 uppercase tracking-wider mb-1 block">Description</label>
            <textarea value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })}
              rows={2} placeholder="Contexte, rôle dans l'écosystème, criticité opérationnelle…" className="noam-input" />
          </div>

          {/* P0 — Type d'asymétrie monétisable */}
          <div className="pt-3 border-t border-white/5">
            <label className="text-[11px] uppercase tracking-wider mb-1.5 block flex items-center gap-1.5 text-[#EF9F27]">
              <Crown className="w-3 h-3" /> Type d'asymétrie monétisable
            </label>
            <div className="grid grid-cols-2 md:grid-cols-5 gap-1.5">
              <button type="button" onClick={() => setForm({ ...form, asymmetry_type: "" })}
                className={`p-2 rounded border text-[10px] uppercase tracking-wider ${!form.asymmetry_type ? "border-white/30 bg-white/5 text-zinc-200" : "border-white/5 text-zinc-500 hover:border-white/15"}`}
                data-testid="um-asym-type-none"
              >—</button>
              {Object.entries(ASYM_TYPES).map(([k, m]) => {
                const Icon = m.icon;
                const active = form.asymmetry_type === k;
                return (
                  <button type="button" key={k} onClick={() => setForm({ ...form, asymmetry_type: k })}
                    className={`p-2 rounded border text-[10px] uppercase tracking-wider flex flex-col items-center gap-1 transition-colors ${
                      active
                        ? "bg-white/10 text-white"
                        : "border-white/5 text-zinc-500 hover:border-white/15 hover:text-zinc-300"
                    }`}
                    style={active ? { boxShadow: `inset 0 0 0 1px ${m.color}60`, color: m.color, background: `${m.color}10` } : undefined}
                    data-testid={`um-asym-type-${k}`}
                    title={m.example}
                  >
                    <Icon className="w-3 h-3" style={active ? { color: m.color } : undefined} />
                    {m.label}
                  </button>
                );
              })}
            </div>
            {form.asymmetry_type && ASYM_TYPES[form.asymmetry_type] && (
              <div className="mt-2 rounded-md border border-white/10 bg-white/[0.03] p-2.5" data-testid="um-asym-explanation">
                <p className="text-[11px] text-zinc-300 leading-relaxed">
                  {ASYM_TYPES[form.asymmetry_type].explanation}
                </p>
                <p className="text-[10px] text-zinc-500 italic mt-1">
                  ex. : {ASYM_TYPES[form.asymmetry_type].example}
                </p>
              </div>
            )}
          </div>

          {/* P0 fractal — Position de capture de valeur dans la chaîne */}
          <div className="pt-3 border-t border-white/5">
            <label className="text-[11px] uppercase tracking-wider mb-1.5 block flex items-center gap-1.5 text-zinc-300">
              Position de capture (fractale)
            </label>
            <p className="text-[10px] text-zinc-500 mb-2 italic">
              Aucune position n'est intrinsèquement supérieure. La rente vient de la non-remplaçabilité, pas d'une hiérarchie top-down.
            </p>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-1.5">
              <button type="button" onClick={() => setForm({ ...form, position_capture: "" })}
                className={`p-2 rounded border text-[10px] uppercase tracking-wider ${!form.position_capture ? "border-white/30 bg-white/5 text-zinc-200" : "border-white/5 text-zinc-500 hover:border-white/15"}`}
                data-testid="um-position-none"
              >—</button>
              {Object.entries(POSITION_CAPTURE).map(([k, m]) => {
                const Icon = m.icon;
                const active = form.position_capture === k;
                return (
                  <button type="button" key={k} onClick={() => setForm({ ...form, position_capture: k })}
                    className={`p-2 rounded border text-[10px] uppercase tracking-wider flex flex-col items-center gap-1 transition-colors ${
                      active
                        ? "bg-white/10 text-white"
                        : "border-white/5 text-zinc-500 hover:border-white/15 hover:text-zinc-300"
                    }`}
                    style={active ? { boxShadow: `inset 0 0 0 1px ${m.color}60`, color: m.color, background: `${m.color}10` } : undefined}
                    data-testid={`um-position-${k}`}
                    title={m.desc}
                  >
                    <Icon className="w-3 h-3" style={active ? { color: m.color } : undefined} />
                    {m.label}
                  </button>
                );
              })}
            </div>
            {form.position_capture && POSITION_CAPTURE[form.position_capture] && (
              <p className="text-[10px] text-zinc-500 italic mt-2">
                {POSITION_CAPTURE[form.position_capture].desc}
              </p>
            )}
          </div>

          {/* P0 — 5 questions diagnostiques Bocchio */}
          <div className="pt-3 border-t border-white/5">
            <button type="button" onClick={() => setShowDx(!showDx)}
              className="text-[11px] uppercase tracking-wider text-zinc-400 hover:text-white flex items-center gap-1.5"
              data-testid="um-toggle-dx"
            >
              <Sparkles className="w-3 h-3" /> Diagnostic fractal · 5 questions {showDx ? "▾" : "▸"}
            </button>
            {showDx && (
              <div className="mt-3 space-y-2.5">
                <DxField label="Qui dépend de moi ?"           value={form.dx_who_depends}       onChange={(v) => setForm({ ...form, dx_who_depends: v })}       color="#378ADD" testid="um-dx-1" />
                <DxField label="De qui je dépends ?"            value={form.dx_who_i_depend_on}    onChange={(v) => setForm({ ...form, dx_who_i_depend_on: v })}    color="#1D9E75" testid="um-dx-2" />
                <DxField label="Si je disparais ?"              value={form.dx_if_i_disappear}     onChange={(v) => setForm({ ...form, dx_if_i_disappear: v })}     color="#EF9F27" testid="um-dx-3" />
                <DxField label="Si eux disparaissent ?"          value={form.dx_if_they_disappear}  onChange={(v) => setForm({ ...form, dx_if_they_disappear: v })}  color="#C26EE7" testid="um-dx-4" />
                <DxField label="Où je capte la valeur (amont / aval / soutien) et pourquoi je suis non-remplaçable ?" value={form.dx_asymmetry_monetized} onChange={(v) => setForm({ ...form, dx_asymmetry_monetized: v })} color="#E24B4A" testid="um-dx-5" highlight />
              </div>
            )}
          </div>

          {err && (
            <div className="flex items-start gap-2 p-2.5 rounded-md border border-[#E24B4A]/30 bg-[#E24B4A]/10 text-[#FCA5A5] text-xs">
              <AlertTriangle className="w-3.5 h-3.5 mt-0.5" /> {err}
            </div>
          )}
          <div className="flex items-center justify-end gap-2 pt-3 border-t border-white/5">
            <button type="button" onClick={onClose} className="btn-ghost text-xs">Annuler</button>
            <button type="submit" disabled={busy} className="btn-primary text-xs disabled:opacity-50" data-testid="um-form-submit">
              {busy ? <><Loader2 className="w-3.5 h-3.5 animate-spin" /> Enregistrement…</> : <>{isEdit ? "Enregistrer" : "Créer"}</>}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

function DxField({ label, value, onChange, color, testid, highlight = false }) {
  return (
    <div>
      <label className="text-[10px] uppercase tracking-wider mb-1 block flex items-center gap-1.5" style={{ color }}>
        <span className="w-1 h-1 rounded-full" style={{ background: color }} />
        {label}
      </label>
      <textarea
        value={value || ""}
        onChange={(e) => onChange(e.target.value)}
        rows={highlight ? 2 : 1}
        placeholder="Réponse en 1-2 phrases…"
        className={`noam-input text-xs resize-none ${highlight ? "bg-[#E24B4A]/5 border-[#E24B4A]/20" : ""}`}
        data-testid={testid}
      />
    </div>
  );
}

// P1 multi-maps — Sélecteur de cartes (onglets + boutons d'action)
function MapSwitcher({ maps, activeMapId, onSelect, onNew, onRename, onEditDoctrine, onDelete }) {
  if (!maps || maps.length === 0) {
    return (
      <div className="noam-card p-2 flex items-center gap-2" data-testid="um-map-switcher">
        <Map className="w-3.5 h-3.5 text-zinc-500" />
        <span className="text-[11px] text-zinc-500 italic flex-1">Aucune carte créée.</span>
        <button onClick={onNew} className="btn-primary text-xs" data-testid="um-map-new-btn">
          <Plus className="w-3 h-3" /> Nouvelle carte
        </button>
      </div>
    );
  }
  const active = maps.find((m) => m.id === activeMapId);
  return (
    <div className="noam-card p-2 flex items-center gap-2 flex-wrap" data-testid="um-map-switcher">
      <Map className="w-3.5 h-3.5 text-zinc-500 flex-shrink-0" />
      <div className="flex items-center gap-1 flex-wrap flex-1 min-w-0">
        {maps.map((m) => {
          const isActive = m.id === activeMapId;
          return (
            <button
              key={m.id}
              onClick={() => onSelect(m.id)}
              className={`text-[11px] px-2.5 py-1 rounded-md border transition-colors whitespace-nowrap inline-flex items-center gap-1.5 ${
                isActive
                  ? "border-[#378ADD]/60 bg-[#378ADD]/15 text-white"
                  : "border-white/10 text-zinc-400 hover:text-white hover:border-white/20"
              }`}
              data-testid={`um-map-tab-${m.id}`}
              title={m.description || m.name}
            >
              {isActive && <span className="w-1 h-1 rounded-full bg-[#378ADD]" />}
              <span className="truncate max-w-[160px]">{m.name}</span>
              <span className="text-[9px] text-zinc-500">· {m.actors_count}</span>
            </button>
          );
        })}
      </div>
      {active && (
        <>
          <button
            onClick={() => onEditDoctrine(active)}
            className="text-zinc-500 hover:text-[#EF9F27] p-1 rounded hover:bg-white/5"
            title="Éditer le texte de doctrine de cette carte"
            data-testid="um-map-edit-doctrine"
          >
            <Crown className="w-3.5 h-3.5" />
          </button>
          <button
            onClick={() => onRename(active)}
            className="text-zinc-500 hover:text-white p-1 rounded hover:bg-white/5"
            title="Renommer cette carte"
            data-testid="um-map-rename"
          >
            <Edit2 className="w-3.5 h-3.5" />
          </button>
          {maps.length > 1 && (
            <button
              onClick={() => onDelete(active)}
              className="text-zinc-500 hover:text-[#E24B4A] p-1 rounded hover:bg-white/5"
              title="Supprimer cette carte"
              data-testid="um-map-delete"
            >
              <Trash2 className="w-3.5 h-3.5" />
            </button>
          )}
          {/* V23.14.7 — Freshness badge pour la carte active */}
          <FreshnessBadge
            lastGeneratedAt={active.updated_at || active.created_at}
            moduleLabel={`Carto · ${active.name}`}
            testid="um-map-freshness"
          />
        </>
      )}
      <button onClick={onNew} className="btn-primary text-xs flex-shrink-0" data-testid="um-map-new-btn">
        <Plus className="w-3 h-3" /> Nouvelle carte
      </button>
    </div>
  );
}

// P0 fractal — Éditeur de carte (création IA hybride, renommage, doctrine custom)
function MapEditor({ mode, map, prefill, activeMapId, onClose, onSaved }) {
  const isNew = mode === "new";
  const isRename = mode === "rename";
  const isDoctrine = mode === "edit-doctrine";
  const [form, setForm] = useState(() => ({
    name: prefill?.name || map?.name || "",
    description: prefill?.description || map?.description || "",
    doctrine_text: map?.doctrine_text || "",
    central_actor: prefill?.central_actor || "",
    central_actor_type: prefill?.central_actor_type || "organisation",
    sector: prefill?.sector || "",
    ai_mode: "hybrid",
    use_ai: true,
  }));
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true); setErr("");
    try {
      if (isNew) {
        if (form.use_ai && form.central_actor.trim()) {
          // Génération IA hybride
          const r = await api.post("/workspace/utility-mapping/maps/generate", {
            name: form.name.trim() || form.central_actor.trim(),
            description: form.description.trim(),
            central_actor: form.central_actor.trim(),
            central_actor_type: form.central_actor_type,
            sector: form.sector.trim(),
            mode: form.ai_mode,
          });
          notify.success(`Carte « ${r.data.map_name} » générée : ${r.data.actors_count} acteurs, ${r.data.relations_count} relations.`);
          onSaved(r.data.map_id);
        } else {
          // Création simple vide
          const r = await api.post("/workspace/utility-mapping/maps", {
            name: form.name.trim() || "Nouvelle carte",
            description: form.description.trim(),
          });
          notify.success("Carte créée.");
          onSaved(r.data.id);
        }
      } else if (isRename) {
        await api.patch(`/workspace/utility-mapping/maps/${map.id}`, {
          name: form.name.trim(),
          description: form.description.trim(),
        });
        notify.success("Carte mise à jour.");
        onSaved();
      } else if (isDoctrine) {
        await api.patch(`/workspace/utility-mapping/maps/${map.id}`, {
          doctrine_text: form.doctrine_text.trim(),
        });
        notify.success("Doctrine mise à jour.");
        onSaved();
      }
    } catch (e) { setErr(fmtError(e, "Opération impossible.")); }
    finally { setBusy(false); }
  };

  const title = isNew ? "Nouvelle carte d'utilité" : isRename ? "Renommer la carte" : "Doctrine de cette carte";

  return (
    <div className="fixed inset-0 z-50 bg-black/70 backdrop-blur flex items-center justify-center p-4 overflow-y-auto" onClick={onClose}>
      <div className="noam-card w-full max-w-2xl my-8" onClick={(e) => e.stopPropagation()} data-testid="um-map-editor-modal">
        <div className="flex items-start justify-between p-5 border-b border-white/5">
          <div>
            <div className="noam-overline flex items-center gap-1.5">
              {isDoctrine ? <Crown className="w-3 h-3 text-[#EF9F27]" /> : <Map className="w-3 h-3 text-[#378ADD]" />}
              {title}
            </div>
            <div className="noam-display text-base font-semibold mt-0.5">
              {isNew ? "Créer une carte" : map?.name}
            </div>
          </div>
          <button onClick={onClose} className="p-1.5 rounded hover:bg-white/5"><X className="w-4 h-4" /></button>
        </div>

        <form onSubmit={submit} className="p-5 space-y-4 max-h-[75vh] overflow-y-auto">
          {(isNew || isRename) && (
            <>
              <div>
                <label className="text-[11px] text-zinc-400 uppercase tracking-wider mb-1 block">Nom de la carte *</label>
                <input
                  required={isRename}
                  value={form.name}
                  onChange={(e) => setForm({ ...form, name: e.target.value })}
                  placeholder="Ex : Écosystème Banque CIC · Mon cabinet · Hôpital XYZ…"
                  className="noam-input"
                  data-testid="um-map-form-name"
                  autoFocus
                />
              </div>
              <div>
                <label className="text-[11px] text-zinc-400 uppercase tracking-wider mb-1 block">Description (optionnelle)</label>
                <input
                  value={form.description}
                  onChange={(e) => setForm({ ...form, description: e.target.value })}
                  placeholder="À quoi sert cette cartographie ?"
                  className="noam-input"
                  data-testid="um-map-form-desc"
                />
              </div>
            </>
          )}

          {isNew && (
            <div className="pt-3 border-t border-white/5 space-y-3">
              <label className="flex items-center gap-2 text-xs text-zinc-300 cursor-pointer">
                <input
                  type="checkbox"
                  checked={form.use_ai}
                  onChange={(e) => setForm({ ...form, use_ai: e.target.checked })}
                  className="accent-[#378ADD]"
                  data-testid="um-map-form-use-ai"
                />
                <Sparkles className="w-3.5 h-3.5 text-[#EF9F27]" />
                <span className="font-semibold">Générer automatiquement avec l'IA hybride (NOAM + Web)</span>
              </label>

              {form.use_ai && (
                <div className="space-y-3 pl-6 border-l-2 border-[#EF9F27]/30">
                  {/* V23.16.13 — Pré-remplissage depuis le positionnement de l'org */}
                  <button
                    type="button"
                    onClick={async () => {
                      try {
                        const r = await api.get("/workspace/positionnement");
                        const me = await api.get("/workspace/me");
                        const p = r.data?.profile || {};
                        const orgName = me.data?.organisation?.name || "";
                        setForm((f) => ({
                          ...f,
                          name: f.name || `Écosystème ${orgName}`,
                          central_actor: orgName,
                          central_actor_type: "organisation",
                          sector: p.sector || p.sector_free || "",
                          description: f.description || (p.description || "").slice(0, 200),
                        }));
                        notify.success("Champs pré-remplis depuis votre positionnement.");
                      } catch (e) {
                        notify.error(fmtError(e, "Impossible de lire le positionnement."));
                      }
                    }}
                    className="inline-flex items-center gap-1.5 text-[11px] px-2 py-1 rounded bg-[#1D9E75]/15 text-[#1D9E75] border border-[#1D9E75]/30 hover:bg-[#1D9E75]/25"
                    data-testid="um-map-form-autofill"
                  >
                    <Sparkles className="w-3 h-3" /> Auto depuis mon org
                  </button>
                  <p className="text-[11px] text-zinc-400 italic">
                    L'IA va déterminer 6-12 acteurs gravitant autour de votre acteur central, leurs types (personne / organisation / système / infra), leurs criticités, et les relations de dépendance (avec position fractale amont/aval/soutien). Vous pourrez tout éditer ensuite.
                  </p>
                  <div>
                    <label className="text-[11px] uppercase tracking-wider mb-1 block flex items-center gap-1.5 text-[#EF9F27]">
                      <Crown className="w-3 h-3" /> Acteur central *
                    </label>
                    <input
                      required={form.use_ai}
                      value={form.central_actor}
                      onChange={(e) => setForm({ ...form, central_actor: e.target.value })}
                      placeholder="Ex : Banque CIC, Mon cabinet, Hôpital de la Tour, Stripe…"
                      className="noam-input"
                      data-testid="um-map-form-central"
                    />
                  </div>
                  <div className="grid grid-cols-2 gap-3">
                    <div>
                      <label className="text-[11px] text-zinc-400 uppercase tracking-wider mb-1 block">Type</label>
                      <select
                        value={form.central_actor_type}
                        onChange={(e) => setForm({ ...form, central_actor_type: e.target.value })}
                        className="noam-input"
                        data-testid="um-map-form-central-type"
                      >
                        <option value="organisation">Organisation</option>
                        <option value="personne">Personne</option>
                        <option value="systeme">Système</option>
                        <option value="infrastructure">Infrastructure</option>
                      </select>
                    </div>
                    <div>
                      <label className="text-[11px] text-zinc-400 uppercase tracking-wider mb-1 block">Secteur (contexte)</label>
                      <input
                        value={form.sector}
                        onChange={(e) => setForm({ ...form, sector: e.target.value })}
                        placeholder="finance, santé, tech, public…"
                        className="noam-input"
                        data-testid="um-map-form-sector"
                      />
                    </div>
                  </div>
                  <div>
                    <label className="text-[11px] text-zinc-400 uppercase tracking-wider mb-1 block">Mode de génération</label>
                    <div className="flex items-center gap-2">
                      {["internal", "web", "hybrid"].map((m) => (
                        <button
                          key={m}
                          type="button"
                          onClick={() => setForm({ ...form, ai_mode: m })}
                          className={`text-[10px] uppercase tracking-wider px-2.5 py-1 rounded border ${
                            form.ai_mode === m
                              ? "border-[#378ADD] bg-[#378ADD]/15 text-[#378ADD]"
                              : "border-white/10 text-zinc-400 hover:text-white"
                          }`}
                          data-testid={`um-map-form-mode-${m}`}
                        >
                          {m === "internal" ? "Interne" : m === "web" ? "Web seul" : "Hybride"}
                        </button>
                      ))}
                    </div>
                    <p className="text-[10px] text-zinc-500 italic mt-1">
                      Hybride combine NOAM + recherche web Tavily — recommandé pour les acteurs externes.
                    </p>
                  </div>
                </div>
              )}
            </div>
          )}

          {isDoctrine && (
            <div>
              <label className="text-[11px] uppercase tracking-wider mb-1 block flex items-center gap-1.5 text-[#EF9F27]">
                <Crown className="w-3 h-3" /> Texte de doctrine fractale
              </label>
              <p className="text-[10px] text-zinc-500 mb-2 italic">
                Personnalisez le texte affiché dans le panneau de droite pour cette carte. Laissez vide pour utiliser le texte par défaut.
              </p>
              <textarea
                value={form.doctrine_text}
                onChange={(e) => setForm({ ...form, doctrine_text: e.target.value })}
                rows={8}
                placeholder="Ex : Dans cet écosystème, la valeur se capture surtout en soutien — les fournisseurs d'infrastructure deviennent indispensables…"
                className="noam-input text-sm leading-relaxed"
                data-testid="um-map-form-doctrine"
                maxLength={2000}
              />
              <div className="text-right text-[10px] text-zinc-600 mt-1">{form.doctrine_text.length}/2000</div>
            </div>
          )}

          {err && (
            <div className="flex items-start gap-2 p-2.5 rounded-md border border-[#E24B4A]/30 bg-[#E24B4A]/10 text-[#FCA5A5] text-xs">
              <AlertTriangle className="w-3.5 h-3.5 mt-0.5" /> {err}
            </div>
          )}

          <div className="flex items-center justify-end gap-2 pt-3 border-t border-white/5">
            <button type="button" onClick={onClose} className="btn-ghost text-xs">Annuler</button>
            <button type="submit" disabled={busy || (isNew && form.use_ai && !form.central_actor.trim())} className="btn-primary text-xs disabled:opacity-50" data-testid="um-map-form-submit">
              {busy ? <><Loader2 className="w-3.5 h-3.5 animate-spin" /> {isNew && form.use_ai ? "Génération IA…" : "Enregistrement…"}</> : (
                <>
                  {isNew && form.use_ai ? <><Sparkles className="w-3.5 h-3.5" /> Générer la carte</> : <><Check className="w-3.5 h-3.5" /> {isNew ? "Créer" : "Enregistrer"}</>}
                </>
              )}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

